/**
 * PreviewPane — the central preview panel (top bar + CV viewport).
 *
 * Hosts the top bar (logo + <Header>) and the CV viewport that renders
 * the user's CV in one of 4 layouts (classique / moderne / ats / pro)
 * or via the Typst WebAssembly preview engine. Also handles margins,
 * page mode, and mobile pinch-zoom.
 *
 * Pure UI component — all state and handlers come from the parent
 * (App.tsx). The JSX body is a verbatim extraction of the original
 * ~840-line block to keep behavior and DOM identical. Splitting each
 * layout into its own sub-component is a follow-up concern.
 */

import React, { lazy, memo, Suspense } from 'react';
import { ScrollProgress } from '../ui/ScrollProgress';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Briefcase,
  GraduationCap,
  Wrench,
  Plus,
  Award,
} from 'lucide-react';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import type { CVData } from '../../types';
import type { getTType } from '../../contexts/I18nContext';
import type { LayoutTemplate, PreviewEngine, Theme, Locale } from '../../hooks/useEditorUi';
import type { FieldVisibility, CommonInfo } from '../../hooks/useCvState';
import type { MarginSize } from './VisualPanel';
import { Header } from '../Header';

const ProLayout = lazy(() =>
  import('../cv-layouts/ProLayout').then((m) => ({ default: m.ProLayout })),
);
const TypstPreview = lazy(() =>
  import('../TypstPreview').then((m) => ({ default: m.TypstPreview })),
);

const marginStyles: Record<MarginSize, { padding: string }> = {
  none: { padding: '0' },
  small: { padding: '12px 15px' },
  medium: { padding: '23px 30px' },
  large: { padding: '40px 50px' },
};

export interface PreviewPaneProps {
  t: getTType;

  // Layout / responsive flags
  isExporting: boolean;
  isMobile: boolean;
  mobileTab: 'edition' | 'ats' | 'preview';
  mobilePreviewSubTab: 'apercu' | 'visuel';

  // Sidebar states (passed to the inner <Header>)
  isSidebarOpen: boolean;
  setIsSidebarOpen: (updater: (prev: boolean) => boolean) => void;
  isAtsSidebarOpen: boolean;
  setIsAtsSidebarOpen: (updater: (prev: boolean) => boolean) => void;

  // App chrome (for <Header>)
  theme: Theme;
  setTheme: (updater: (prev: Theme) => Theme) => void;
  locale: Locale;
  setLocale: (updater: (prev: Locale) => Locale) => void;
  authUser: SupabaseUser | null;
  authSignOut: () => Promise<void> | void;
  setIsLoginModalOpen: (next: boolean) => void;
  cvVersionCount?: number;
  templateMeta?: import('../../hooks/useCvState').TemplateMetaItem[];
  activeTemplate?: string;
  setActiveTemplate?: (id: string) => void;

  // CV preview data
  data: CVData;
  commonInfo: CommonInfo;
  fieldVisibility: FieldVisibility;
  layoutTemplate: LayoutTemplate;
  previewEngine: PreviewEngine;

  // a11y: the "sr-only labels" toggle from <OptionsSection>
  srOnlyLabelsEnabled: boolean;

  // Format helpers (stay in App.tsx because they depend on i18n / shared with export handlers)
  getFormattedLocation: () => string;
  getBornPrefix: () => string;
  getContactLineATS: () => string;
  calculateAge: (birthDate: string) => number | null;
  formatDateWithFullMonth: (dateStr: string) => string;
  formatEducationDate: (edu: { date?: string; startDate?: string; endDate?: string }) => string;
  extractCityFromCompany: (company: string) => string;

  // Single-page height constant (297 mm in px) used to paginate the preview.
  PAGE_HEIGHT_PX: number;

  // Preview config
  marginSize: MarginSize;
  pageMode: boolean;
  pagesToShow: number;
  totalPages: number;
  previewUserScale: number;

  // Refs
  cvRef: React.RefObject<HTMLDivElement | null>;
  previewViewportRef: React.RefObject<HTMLDivElement | null>;

  // Touch handlers (mobile pinch-zoom)
  handlePreviewTouchStart: (e: React.TouchEvent) => void;
  handlePreviewTouchEnd: (e: React.TouchEvent) => void;

}

export const PreviewPane = memo(function PreviewPane(props: PreviewPaneProps) {
  // Destructure everything so the inlined JSX references plain locals
  // identical to what App.tsx used to expose.
  const {
    t,
    isExporting,
    isMobile,
    mobileTab,
    mobilePreviewSubTab,
    isSidebarOpen,
    setIsSidebarOpen,
    isAtsSidebarOpen,
    setIsAtsSidebarOpen,
    theme,
    setTheme,
    locale,
    setLocale,
    authUser,
    authSignOut,
    setIsLoginModalOpen,
    data,
    commonInfo,
    fieldVisibility,
    layoutTemplate,
    previewEngine,
    srOnlyLabelsEnabled,
    getFormattedLocation,
    getBornPrefix,
    getContactLineATS,
    calculateAge,
    formatDateWithFullMonth,
    formatEducationDate,
    extractCityFromCompany,
    PAGE_HEIGHT_PX,
    marginSize,
    pageMode,
    pagesToShow,
    totalPages,
    previewUserScale,
    cvRef,
    previewViewportRef,
    handlePreviewTouchStart,
    handlePreviewTouchEnd,
  } = props;
  // Silence unused-var warnings for the variables that only the inline
  // JSX reads — the TypeScript narrowing needs them destructured.
  void t;
  void data;
  void commonInfo;
  void fieldVisibility;
  void layoutTemplate;
  void previewEngine;
  void srOnlyLabelsEnabled;
  void getFormattedLocation;
  void getBornPrefix;
  void getContactLineATS;
  void calculateAge;
  void formatDateWithFullMonth;
  void formatEducationDate;
  void extractCityFromCompany;
  void PAGE_HEIGHT_PX;
  void pageMode;
  void pagesToShow;
  void totalPages;
  void cvRef;

  return (
    <div
      className={`flex-1 min-w-0 preview-panel overflow-y-auto h-screen md:h-screen flex flex-col relative ${isExporting ? 'p-0 h-auto overflow-visible' : ''} ${isMobile ? `absolute left-0 right-0 bottom-0 mobile-panel-top ${mobileTab === 'preview' && mobilePreviewSubTab === 'apercu' ? 'z-10 flex flex-col' : 'z-0 opacity-0 pointer-events-none invisible'}` : ''
        }`}
    >
      {!isExporting && <ScrollProgress targetSelector=".preview-panel" />}
      {!isExporting && !isMobile && (
        <div className={`preview-top-bar flex items-center justify-center gap-3 p-2 md:p-4 border-b border-white/10 sticky top-0 z-20 backdrop-blur-sm flex-wrap ${theme === 'light' ? 'bg-[var(--app-bg)]/95' : 'bg-[#0a0a0a]/95'}`}>
          <div className="flex items-center shrink-0 mr-1">
            <div className="logo-crop w-24 h-8 overflow-hidden rounded-lg flex-shrink-0 bg-white/5 hidden md:block">
              <img src={theme === 'dark' ? '/logo-rectangle-dark.png' : '/logo-rectangle-light.png'} alt="ATSmyCV" className="w-full h-full object-cover" />
            </div>
          </div>
          <Header
            theme={theme}
            locale={locale}
            isSidebarOpen={isSidebarOpen}
            isAtsSidebarOpen={isAtsSidebarOpen}
            user={authUser}
            cvVersionCount={props.cvVersionCount}
            templateMeta={props.templateMeta}
            activeTemplate={props.activeTemplate}
            setActiveTemplate={props.setActiveTemplate}
            onThemeToggle={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
            onLocaleToggle={() => setLocale((prev) => (prev === 'fr' ? 'en' : 'fr'))}
            onSidebarToggle={() => setIsSidebarOpen((prev) => !prev)}
            onAtsSidebarToggle={() => setIsAtsSidebarOpen((prev) => !prev)}
            onLoginClick={() => setIsLoginModalOpen(true)}
            onLogoutClick={() => { void authSignOut(); }}
          />
        </div>
      )}
      <div
        ref={previewViewportRef}
        className={`flex-1 overflow-auto overflow-x-auto overflow-y-auto flex flex-col items-center py-6 md:py-10 px-2 md:px-4 scrollbar-hide cv-preview-viewport ${isExporting ? 'cv-export-scroll p-0 h-auto overflow-visible' : ''}`}
        style={isMobile ? ({ '--preview-user-scale': previewUserScale } as React.CSSProperties) : undefined}
        onTouchStart={isMobile ? handlePreviewTouchStart : undefined}
        onTouchEnd={isMobile ? handlePreviewTouchEnd : undefined}
      >
        <div className="cv-preview-scale-container">
          <div className="cv-preview-zoom-wrapper">
            <div className="cv-preview-inner flex flex-col items-center w-full" style={{ minWidth: 794, width: isMobile ? 794 : undefined }}>
              {previewEngine === 'typst' ? (
                <Suspense fallback={<div className="w-full aspect-[1/1.4142] bg-zinc-800 animate-pulse" />}>
                  <TypstPreview
                    cvData={data}
                    layout={layoutTemplate}
                    options={{ fieldVisibility, marginSize, pageMode: true }}
                    enabled
                    className="min-w-0 w-full"
                  />
                </Suspense>
              ) : layoutTemplate === 'ats' ? (
                /* ATS — Page (preview only) ou Continu (preview + export PDF) */
                pageMode && !isExporting ? (
                  // Mode page: wrapper le contenu dans des pages (uniquement preview)
                  <div className="cv-pages-container">
                    {Array.from({ length: pagesToShow }).map((_, pageIndex) => (
                      <div key={pageIndex} className="cv-page-box" data-page={`${pageIndex + 1} / ${totalPages}`}>
                        <div
                          className="cv-page-content"
                          style={{ marginTop: `-${pageIndex * PAGE_HEIGHT_PX}px` }}
                        >
                          <div
                            ref={pageIndex === 0 ? cvRef : undefined}
                            className="cv-container cv-ats-mode"
                            style={{ background: '#fff', color: '#000', fontFamily: 'Arial, sans-serif', ...marginStyles[marginSize] }}
                          >
                            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                              <header style={{ marginBottom: '10px', borderBottom: '1px solid #000', paddingBottom: '6px' }}>
                                {((fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.')) || (fieldVisibility.firstName && data.personalInfo.firstName) || (fieldVisibility.lastName && data.personalInfo.lastName)) ? (
                                  <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px 0', textTransform: 'uppercase' }}>
                                    {fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.') && <>{data.personalInfo.civility} </>}
                                    {srOnlyLabelsEnabled && <span className="sr-only">{t('profile.firstName')}: </span>}
                                    {fieldVisibility.firstName && data.personalInfo.firstName}
                                    {(fieldVisibility.firstName && data.personalInfo.firstName) && (fieldVisibility.lastName && data.personalInfo.lastName) ? ' ' : null}
                                    {srOnlyLabelsEnabled && <span className="sr-only">{t('profile.lastName')}: </span>}
                                    {fieldVisibility.lastName && data.personalInfo.lastName}
                                  </h1>
                                ) : null}
                                <h2 style={{ fontSize: '16px', fontWeight: 'normal', margin: '0 0 8px 0', color: '#333' }}>{data.personalInfo.title}</h2>
                                {getContactLineATS() && <p style={{ fontSize: '11px', margin: '0', color: '#333' }}>{getContactLineATS()}</p>}
                              </header>
                              <section style={{ marginBottom: '8px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>{t('sections.profil')}</h3>
                                <p style={{ fontSize: '11px', lineHeight: '1.5', margin: '0', textAlign: 'justify' }}>{data.personalInfo.summary}</p>
                              </section>
                              <section style={{ marginBottom: '8px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>{t('sections.experiences')}</h3>
                                {data.experience?.map((exp, i) => {
                                  const city = extractCityFromCompany(exp.company);
                                  const companyName = city ? exp.company.replace(`, ${city}`, '').trim() : exp.company;
                                  return (
                                    <div key={i} style={{ marginBottom: '10px' }}>
                                      <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '0' }}>
                                        {exp.position}
                                        {companyName && <span style={{ fontWeight: 'normal' }}>, {t('experience.atCompany')} {companyName}</span>}
                                        {city && <span style={{ fontWeight: 'normal', color: '#666' }}>, {t('experience.inCity')} {city}</span>}
                                        <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal' }}>, {formatDateWithFullMonth(exp.startDate)} {t('experience.dateTo')} {formatDateWithFullMonth(exp.endDate)}</span>
                                      </p>
                                      <p style={{ fontSize: '10px', lineHeight: '1.4', margin: '0', whiteSpace: 'pre-wrap' }}>{exp.description}</p>
                                    </div>
                                  );
                                })}
                              </section>
                              <section style={{ marginBottom: '8px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>{t('sections.formation')}</h3>
                                {data.education?.map((edu, i) => (
                                  <div key={i} style={{ marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                      <strong style={{ fontSize: '11px' }}>{edu.degree}</strong>
                                      <span style={{ fontSize: '10px', color: '#666' }}>{formatEducationDate(edu)}</span>
                                    </div>
                                    <p style={{ fontSize: '10px', margin: '2px 0 0 0', color: '#444' }}>{edu.school}</p>
                                  </div>
                                ))}
                              </section>
                              <section style={{ marginBottom: '8px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>{t('skills.label')}</h3>
                                <p style={{ fontSize: '11px', lineHeight: '1.6', margin: '0' }}>{data.skills?.join(' • ')}</p>
                              </section>
                              <section style={{ marginBottom: '8px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>{t('languagesLabel')}</h3>
                                <p style={{ fontSize: '11px', lineHeight: '1.6', margin: '0' }}>{data.languages?.join(' • ')}</p>
                              </section>
                              {data.hobbies && data.hobbies.length > 0 && (
                                <section>
                                  <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>{t('hobbies.label')}</h3>
                                  <p style={{ fontSize: '11px', lineHeight: '1.6', margin: '0' }}>{data.hobbies?.join(' • ')}</p>
                                </section>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Mode continu
                  <div>
                    <div
                      ref={cvRef}
                      className="cv-container cv-ats-mode"
                      style={{ background: '#fff', color: '#000', fontFamily: 'Arial, sans-serif', ...marginStyles[marginSize] }}
                    >
                      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        {/* Header - Simple text, champs visibles. ATS: Prénom/Nom en sr-only si activé. */}
                        <header style={{ marginBottom: '10px', borderBottom: '1px solid #000', paddingBottom: '6px' }}>
                          {((fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.')) || (fieldVisibility.firstName && data.personalInfo.firstName) || (fieldVisibility.lastName && data.personalInfo.lastName)) ? (
                            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px 0', textTransform: 'uppercase' }}>
                              {fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.') && <>{data.personalInfo.civility} </>}
                              {srOnlyLabelsEnabled && <span className="sr-only">{t('profile.firstName')}: </span>}
                              {fieldVisibility.firstName && data.personalInfo.firstName}
                              {(fieldVisibility.firstName && data.personalInfo.firstName) && (fieldVisibility.lastName && data.personalInfo.lastName) ? ' ' : null}
                              {srOnlyLabelsEnabled && <span className="sr-only">{t('profile.lastName')}: </span>}
                              {fieldVisibility.lastName && data.personalInfo.lastName}
                            </h1>
                          ) : null}
                          <h2 style={{ fontSize: '16px', fontWeight: 'normal', margin: '0 0 8px 0', color: '#333' }}>
                            {data.personalInfo.title}
                          </h2>
                          {getContactLineATS() && (
                            <p style={{ fontSize: '11px', margin: '0', color: '#333' }}>
                              {getContactLineATS()}
                            </p>
                          )}
                        </header>
    
                        {/* Summary */}
                        <section style={{ marginBottom: '8px' }}>
                          <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>
                            {t('sections.profil')}
                          </h3>
                          <p style={{ fontSize: '11px', lineHeight: '1.5', margin: '0', textAlign: 'justify' }}>
                            {data.personalInfo.summary}
                          </p>
                        </section>

                        {/* Experience */}
                        <section style={{ marginBottom: '8px' }}>
                          <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>
                            {t('sections.experiences')}
                          </h3>
                          {data.experience?.map((exp, i) => {
                            const city = extractCityFromCompany(exp.company);
                            const companyName = city ? exp.company.replace(`, ${city}`, '').trim() : exp.company;
                            const formattedStartDate = formatDateWithFullMonth(exp.startDate);
                            const formattedEndDate = formatDateWithFullMonth(exp.endDate);

                            return (
                              <div key={i} style={{ marginBottom: '10px' }}>
                                <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '0' }}>
                                  {exp.position}
                                  {companyName && <span style={{ fontWeight: 'normal' }}>, {t('experience.atCompany')} {companyName}</span>}
                                  {city && <span style={{ fontWeight: 'normal', color: '#666' }}>, {t('experience.inCity')} {city}</span>}
                                  {(formattedStartDate || formattedEndDate) && (
                                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                                      , {formattedStartDate} {t('experience.dateTo')} {formattedEndDate}
                                    </span>
                                  )}
                                </p>
                                <p style={{ fontSize: '10px', lineHeight: '1.4', margin: '0', whiteSpace: 'pre-wrap' }}>
                                  {exp.description}
                                </p>
                              </div>
                            );
                          })}
                        </section>

                        {/* Education */}
                        <section style={{ marginBottom: '8px' }}>
                          <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>
                            {t('sections.formation')}
                          </h3>
                          {data.education?.map((edu, i) => (
                            <div key={i} style={{ marginBottom: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <strong style={{ fontSize: '11px' }}>{edu.degree}</strong>
                                <span style={{ fontSize: '10px', color: '#666' }}>{formatEducationDate(edu)}</span>
                              </div>
                              <p style={{ fontSize: '10px', margin: '2px 0 0 0', color: '#444' }}>{edu.school}</p>
                            </div>
                          ))}
                        </section>

                        {/* Skills */}
                        <section style={{ marginBottom: '8px' }}>
                          <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>
                            {t('skills.label')}
                          </h3>
                          <p style={{ fontSize: '11px', lineHeight: '1.6', margin: '0' }}>
                            {data.skills?.join(' • ')}
                          </p>
                        </section>

                        {/* Languages */}
                        <section style={{ marginBottom: '8px' }}>
                          <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>
                            {t('languagesLabel')}
                          </h3>
                          <p style={{ fontSize: '11px', lineHeight: '1.6', margin: '0' }}>
                            {data.languages?.join(' • ')}
                          </p>
                        </section>

                        {/* Hobbies */}
                        {data.hobbies && data.hobbies.length > 0 && (
                          <section>
                            <h3 style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '3px' }}>
                              {t('hobbies.label')}
                            </h3>
                            <p style={{ fontSize: '11px', lineHeight: '1.6', margin: '0' }}>
                              {data.hobbies?.join(' • ')}
                            </p>
                          </section>
                        )}
                      </div>
                    </div>
                  </div>
                )
              ) : layoutTemplate === 'pro' ? (
                /* PRO — Page (preview) ou Continu */
                pageMode && !isExporting ? (
                  <div className="cv-pages-container">
                    {Array.from({ length: pagesToShow }).map((_, pageIndex) => (
                      <div key={pageIndex} className="cv-page-box" data-page={`${pageIndex + 1} / ${totalPages}`}>
                        <div className="cv-page-content" style={{ marginTop: `-${pageIndex * PAGE_HEIGHT_PX}px` }}>
                          <div ref={pageIndex === 0 ? cvRef : undefined} className="w-[794px] min-h-[1123px] mx-auto bg-white relative">
                            <Suspense fallback={<div className="w-full h-[1123px] bg-zinc-800 animate-pulse" />}>
                              <ProLayout data={data} fieldVisibility={fieldVisibility} commonInfo={commonInfo} t={t} marginSize={marginSize} />
                            </Suspense>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div ref={cvRef} className="w-[794px] mx-auto shadow-2xl print:shadow-none bg-white relative shrink-0">
                    <Suspense fallback={<div className="w-full h-[1123px] bg-zinc-800 animate-pulse" />}>
                      <ProLayout data={data} fieldVisibility={fieldVisibility} commonInfo={commonInfo} t={t} marginSize={marginSize} />
                    </Suspense>
                  </div>
                )
              ) : layoutTemplate === 'moderne' ? (
                /* Moderne — Page ou Continu */
                pageMode && !isExporting ? (
                  <div className="cv-pages-container">
                    {Array.from({ length: pagesToShow }).map((_, pageIndex) => (
                      <div key={pageIndex} className="cv-page-box" data-page={`${pageIndex + 1} / ${totalPages}`}>
                        <div className="cv-page-content" style={{ marginTop: `-${pageIndex * PAGE_HEIGHT_PX}px` }}>
                          <div ref={pageIndex === 0 ? cvRef : undefined} className="cv-container relative" style={{ background: '#fff', color: '#0f172a', ...marginStyles[marginSize] }}>
                            <div className="flex min-h-full">
                              {/* Colonne gauche — contact, compétences, formation, langues, passions */}
                              <aside className="w-52 shrink-0 bg-slate-800 text-white p-5 rounded-r-2xl print:rounded-none">
                                <header className="mb-4">
                                  {((fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.')) || (fieldVisibility.firstName && data.personalInfo.firstName) || (fieldVisibility.lastName && data.personalInfo.lastName)) ? (
                                    <h1 className="text-lg font-bold leading-tight text-white">
                                      {fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.') && <>{data.personalInfo.civility} </>}
                                      {fieldVisibility.firstName && data.personalInfo.firstName} {fieldVisibility.lastName && data.personalInfo.lastName}
                                    </h1>
                                  ) : null}
                                  <p className="text-green-500 text-sm font-medium mt-0.5">{data.personalInfo.title}</p>
                                </header>
                                {fieldVisibility.avatar && (data.personalInfo.avatar || commonInfo.compressedAvatar) && (
                                  <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white/20 mb-4">
                                    <img src={data.personalInfo.avatar || commonInfo.compressedAvatar} alt={`${data.personalInfo?.firstName ?? ''} ${data.personalInfo?.lastName ?? ''}`.trim() || 'Photo de profil'} className="w-full h-full object-cover" />
                                  </div>
                                )}
                                <section className="mb-4">
                                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">Contact</h3>
                                  <div className="space-y-1.5 text-sm">
                                    {fieldVisibility.email && data.personalInfo.email && <p className="flex items-center gap-2"><Mail size={10} className="text-green-500 shrink-0" />{data.personalInfo.email}</p>}
                                    {fieldVisibility.phone && data.personalInfo.phone && <p className="flex items-center gap-2"><Phone size={10} className="text-green-500 shrink-0" />{data.personalInfo.phone}</p>}
                                    {(fieldVisibility.city || fieldVisibility.country) && getFormattedLocation() && <p className="flex items-center gap-2"><MapPin size={10} className="text-green-500 shrink-0" />{getFormattedLocation()}</p>}
                                  </div>
                                </section>
                                <section className="mb-4">
                                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('skills.label')}</h3>
                                  <div className="flex flex-wrap gap-1.5">
                                    {data.skills?.map((s, i) => <span key={i} className="text-sm bg-white/10 px-2 py-0.5 rounded">{s}</span>)}
                                  </div>
                                </section>
                                <section className="mb-4">
                                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('sections.formation')}</h3>
                                  <div className="space-y-2 text-sm">
                                    {data.education?.map((edu, i) => (
                                      <div key={i}>
                                        <p className="font-semibold text-white">{edu.degree}</p>
                                        <p className="text-slate-400 text-sm">{edu.school} — {formatEducationDate(edu)}</p>
                                      </div>
                                    ))}
                                  </div>
                                </section>
                                <section className="mb-4">
                                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('languagesLabel')}</h3>
                                  <p className="text-sm text-slate-300">{data.languages?.join(' · ')}</p>
                                </section>
                                {data.hobbies && data.hobbies.length > 0 && (
                                  <section>
                                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('hobbies.label')}</h3>
                                    <p className="text-sm text-slate-300">{data.hobbies.join(' · ')}</p>
                                  </section>
                                )}
                              </aside>
                              <div className="flex-1 p-6 pl-8">
                                <section className="mb-5">
                                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-1">{t('sections.profil')}</h3>
                                  <p className="text-slate-700 text-[13px] leading-relaxed">{data.personalInfo.summary}</p>
                                </section>
                                <section>
                                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">{t('sections.experiences')}</h3>
                                  <div className="space-y-4">
                                    {data.experience?.map((exp, i) => {
                                      const city = extractCityFromCompany(exp.company);
                                      const companyName = city ? exp.company.replace(`, ${city}`, '').trim() : exp.company;
                                      return (
                                        <div key={i}>
                                          <h4 className="text-sm font-bold text-slate-900">{exp.position}</h4>
                                          <p className="text-sm text-slate-500">
                                            {companyName && <span>{companyName}</span>}
                                            {city && <span>{companyName ? ', ' : ''}{city}</span>}
                                            <span className="text-slate-400"> — {formatDateWithFullMonth(exp.startDate)} {t('experience.dateTo')} {formatDateWithFullMonth(exp.endDate)}</span>
                                          </p>
                                          <p className="text-[12px] text-slate-600 whitespace-pre-wrap mt-1">{exp.description}</p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </section>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Moderne continu */
                  <div>
                    <div ref={cvRef} className="cv-container relative" style={{ background: '#fff', color: '#0f172a', ...marginStyles[marginSize] }}>
                      <div className="flex min-h-full">
                        {/* Colonne gauche */}
                        <aside className="w-52 shrink-0 bg-slate-800 text-white p-5 rounded-r-2xl print:rounded-none">
                          <header className="mb-4">
                            {((fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.')) || (fieldVisibility.firstName && data.personalInfo.firstName) || (fieldVisibility.lastName && data.personalInfo.lastName)) ? (
                              <h1 className="text-lg font-bold leading-tight text-white">
                                {fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.') && <>{data.personalInfo.civility} </>}
                                {fieldVisibility.firstName && data.personalInfo.firstName} {fieldVisibility.lastName && data.personalInfo.lastName}
                              </h1>
                            ) : null}
                            <p className="text-green-500 text-sm font-medium mt-0.5">{data.personalInfo.title}</p>
                          </header>
                          {fieldVisibility.avatar && (data.personalInfo.avatar || commonInfo.compressedAvatar) && (
                            <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white/20 mb-4">
                              <img src={data.personalInfo.avatar || commonInfo.compressedAvatar} alt="" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <section className="mb-4">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">Contact</h3>
                            <div className="space-y-1.5 text-sm">
                              {fieldVisibility.email && data.personalInfo.email && <p className="flex items-center gap-2"><Mail size={10} className="text-green-500 shrink-0" />{data.personalInfo.email}</p>}
                              {fieldVisibility.phone && data.personalInfo.phone && <p className="flex items-center gap-2"><Phone size={10} className="text-green-500 shrink-0" />{data.personalInfo.phone}</p>}
                              {(fieldVisibility.city || fieldVisibility.country) && getFormattedLocation() && <p className="flex items-center gap-2"><MapPin size={10} className="text-green-500 shrink-0" />{getFormattedLocation()}</p>}
                            </div>
                          </section>
                          <section className="mb-4">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('skills.label')}</h3>
                            <div className="flex flex-wrap gap-1.5">{data.skills?.map((s, i) => <span key={i} className="text-sm bg-white/10 px-2 py-0.5 rounded">{s}</span>)}</div>
                          </section>
                          <section className="mb-4">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('sections.formation')}</h3>
                            <div className="space-y-2 text-sm">{data.education?.map((edu, i) => (<div key={i}><p className="font-semibold text-white">{edu.degree}</p><p className="text-slate-400 text-sm">{edu.school} — {formatEducationDate(edu)}</p></div>))}</div>
                          </section>
                          <section className="mb-4">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('languagesLabel')}</h3>
                            <p className="text-sm text-slate-300">{data.languages?.join(' · ')}</p>
                          </section>
                          {data.hobbies && data.hobbies.length > 0 && (<section><h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">{t('hobbies.label')}</h3><p className="text-sm text-slate-300">{data.hobbies.join(' · ')}</p></section>)}
                        </aside>
                        <div className="flex-1 p-6 pl-8">
                          <section className="mb-5"><h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-1">{t('sections.profil')}</h3><p className="text-slate-700 text-[13px] leading-relaxed">{data.personalInfo.summary}</p></section>
                          <section><h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">{t('sections.experiences')}</h3><div className="space-y-4">{data.experience?.map((exp, i) => { const city = extractCityFromCompany(exp.company); const companyName = city ? exp.company.replace(`, ${city}`, '').trim() : exp.company; return (<div key={i}><h4 className="text-sm font-bold text-slate-900">{exp.position}</h4><p className="text-sm text-slate-500">{companyName && <span>{companyName}</span>}{city && <span>{companyName ? ', ' : ''}{city}</span>}<span className="text-slate-400"> — {formatDateWithFullMonth(exp.startDate)} {t('experience.dateTo')} {formatDateWithFullMonth(exp.endDate)}</span></p><p className="text-[12px] text-slate-600 whitespace-pre-wrap mt-1">{exp.description}</p></div>); })}</div></section>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                // Graphique Classique — Page (preview only) ou Continu (preview + export PDF)
                pageMode && !isExporting ? (
                  // Mode page: wrapper le contenu dans des pages (uniquement preview)
                  <div className="cv-pages-container">
                    {Array.from({ length: pagesToShow }).map((_, pageIndex) => (
                      <div key={pageIndex} className="cv-page-box" data-page={`${pageIndex + 1} / ${totalPages}`}>
                        <div
                          className="cv-page-content"
                          style={{ marginTop: `-${pageIndex * PAGE_HEIGHT_PX}px` }}
                        >
                          <div
                            ref={pageIndex === 0 ? cvRef : undefined}
                            className="cv-container relative"
                            style={{ background: '#fff', color: '#1a1a1a', ...marginStyles[marginSize] }}
                          >
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 -translate-y-1/2 translate-x-1/2 rounded-full z-0 opacity-50 print-hidden-ats"></div>
                            <div className="relative z-10 flex flex-col min-h-full">
                              <header className="mb-2 flex justify-between items-start border-b-2 border-zinc-900 pb-2">
                                <div className="flex-1">
                                  {((fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.')) || (fieldVisibility.firstName && data.personalInfo.firstName) || (fieldVisibility.lastName && data.personalInfo.lastName)) ? (
                                    <h1 className="text-4xl font-black text-zinc-900 tracking-tighter uppercase leading-none mb-1">
                                      {fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.') && <>{data.personalInfo.civility} </>}
                                      {fieldVisibility.firstName && data.personalInfo.firstName}
                                      {(fieldVisibility.firstName && data.personalInfo.firstName) && (fieldVisibility.lastName && data.personalInfo.lastName) ? ' ' : null}
                                      {fieldVisibility.lastName && data.personalInfo.lastName}
                                    </h1>
                                  ) : null}
                                  <h2 className="text-xl font-bold text-blue-600 flex items-center gap-2">
                                    {data.personalInfo.title}
                                    <div className="h-0.5 bg-blue-600 flex-1 opacity-20"></div>
                                  </h2>
                                </div>
                                {fieldVisibility.avatar && (data.personalInfo.avatar || commonInfo.compressedAvatar) && (
                                  <div className="w-24 h-24 ml-8 relative">
                                    <div className="absolute inset-0 bg-blue-600 rounded-3xl rotate-6 -z-10 translate-x-1 translate-y-1 scale-95 opacity-20 print:hidden"></div>
                                    <div className="w-full h-full rounded-2xl overflow-hidden border-4 border-white shadow-xl print:shadow-none bg-zinc-50">
                                      <img src={data.personalInfo.avatar || commonInfo.compressedAvatar} alt="Avatar" className="w-full h-full object-cover" />
                                    </div>
                                  </div>
                                )}
                              </header>
                              <div className="grid grid-cols-12 gap-6 flex-1">
                                <div className="col-span-8 space-y-4">
                                  <section>
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="w-6 h-6 bg-zinc-900 text-white rounded flex items-center justify-center"><Award size={14} /></div>
                                      <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-zinc-900">{t('sections.profil')}</h3>
                                    </div>
                                    <div className="bg-zinc-50 p-4 rounded-2xl border-l-4 border-zinc-900 relative">
                                      <div className="absolute top-2 right-4 text-4xl text-zinc-200 font-serif opacity-50">"</div>
                                      <p className="text-zinc-600 leading-snug text-[13px] font-medium italic">{data.personalInfo.summary}</p>
                                    </div>
                                  </section>
                                  <section>
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-6 h-6 bg-zinc-900 text-white rounded flex items-center justify-center"><Briefcase size={14} /></div>
                                      <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-zinc-900">{t('sections.experiences')}</h3>
                                    </div>
                                    <div className="space-y-2">
                                      {data.experience?.map((exp, i) => {
                                        const city = extractCityFromCompany(exp.company);
                                        const companyName = city ? exp.company.replace(`, ${city}`, '').trim() : exp.company;
                                        const formattedStartDate = formatDateWithFullMonth(exp.startDate);
                                        const formattedEndDate = formatDateWithFullMonth(exp.endDate);
                                        return (
                                          <div key={i} className="group cursor-default mb-2">
                                            <div className="mb-0.5">
                                              <h4 className="text-[14px] font-black text-zinc-900 group-hover:text-blue-600 transition-colors uppercase tracking-tight leading-tight">{exp.position}</h4>
                                              {(companyName || city || formattedStartDate || formattedEndDate) && (
                                                <p className="text-sm text-zinc-500 leading-tight">
                                                  {companyName && <span className="text-zinc-600 font-medium">{t('experience.atCompany')} {companyName}</span>}
                                                  {city && <span>{companyName ? ', ' : ''}{t('experience.inCity')} {city}</span>}
                                                  {(formattedStartDate || formattedEndDate) && (
                                                    <span className="text-zinc-400">{(companyName || city) ? ', ' : ''}{formattedStartDate} {t('experience.dateTo')} {formattedEndDate}</span>
                                                  )}
                                                </p>
                                              )}
                                            </div>
                                            <p className="text-zinc-600 text-[11.5px] whitespace-pre-wrap leading-tight">{exp.description}</p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </section>
                                </div>
                                <div className="col-span-4 space-y-3">
                                  <section className="bg-zinc-100 p-2.5 rounded-2xl border border-zinc-200">
                                    <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-1.5 flex items-center gap-2"><User size={12} /> Contact</h3>
                                    <div className="space-y-2">
                                      {fieldVisibility.email && data.personalInfo.email && (
                                        <div className="flex items-center gap-3">
                                          <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><Mail size={10} /></div>
                                          <span className="text-sm font-bold text-zinc-600">{data.personalInfo.email}</span>
                                        </div>
                                      )}
                                      {fieldVisibility.phone && data.personalInfo.phone && (
                                        <div className="flex items-center gap-3">
                                          <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><Phone size={10} /></div>
                                          <span className="text-sm font-bold text-zinc-600">{data.personalInfo.phone}</span>
                                        </div>
                                      )}
                                      {(fieldVisibility.city || fieldVisibility.country) && getFormattedLocation() && (
                                        <div className="flex items-center gap-3">
                                          <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><MapPin size={10} /></div>
                                          <span className="text-sm font-bold text-zinc-600">{getFormattedLocation()}</span>
                                        </div>
                                      )}
                                      {(fieldVisibility.birthPlace && data.personalInfo.birthPlace) ? (
                                        <div className="flex items-center gap-3">
                                          <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><MapPin size={10} /></div>
                                          <span className="text-sm font-bold text-zinc-600">{getBornPrefix()} à {data.personalInfo.birthPlace}{fieldVisibility.nationality && data.personalInfo.nationality ? `, ${data.personalInfo.nationality}` : ''}</span>
                                        </div>
                                      ) : fieldVisibility.nationality && data.personalInfo.nationality ? (
                                        <div className="flex items-center gap-3">
                                          <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><Globe size={10} /></div>
                                          <span className="text-sm font-bold text-zinc-600">{data.personalInfo.nationality}</span>
                                        </div>
                                      ) : null}
                                      {(() => {
                                        const personalParts: string[] = [];
                                        if (fieldVisibility.gender && data.personalInfo.gender) personalParts.push(data.personalInfo.gender);
                                        if (fieldVisibility.birthDate && data.personalInfo.birthDate) {
                                          const age = calculateAge(data.personalInfo.birthDate);
                                          personalParts.push(age ? `${age} ans` : data.personalInfo.birthDate);
                                        }
                                        if (fieldVisibility.maritalStatus && data.personalInfo.maritalStatus) personalParts.push(data.personalInfo.maritalStatus);
                                        return personalParts.length > 0 ? (
                                          <div className="flex items-center gap-3">
                                            <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><User size={10} /></div>
                                            <span className="text-sm font-bold text-zinc-600">{personalParts.join(', ')}</span>
                                          </div>
                                        ) : null;
                                      })()}
                                    </div>
                                  </section>
                                  <section className="bg-zinc-900 text-white p-3.5 rounded-3xl shadow-xl print:shadow-none">
                                    <h3 className="text-sm font-black uppercase tracking-[0.2em] text-blue-400 mb-1.5 flex items-center gap-2"><Wrench size={12} /> {t('skills.label')}</h3>
                                    <div className="space-y-2">
                                      {data.skills?.map((skill, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                          <span className="text-sm font-bold text-zinc-300 uppercase leading-none">{skill}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </section>
                                  <section className="px-2">
                                    <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-2 flex items-center gap-2"><GraduationCap size={14} /> {t('sections.formation')}</h3>
                                    <div className="space-y-1.5">
                                      {data.education?.map((edu, i) => (
                                        <div key={i} className="relative pl-3 border-l-2 border-zinc-100">
                                          <h4 className="font-black text-zinc-900 text-sm uppercase tracking-tight leading-tight mb-0.5">{edu.degree}</h4>
                                          <div className="text-zinc-400 font-bold text-[9px] mb-0.5">{edu.school}</div>
                                          <div className="text-[9px] font-black text-blue-600 border border-blue-100 px-1 inline-block rounded">{formatEducationDate(edu)}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </section>
                                  <section className="px-2">
                                    <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-2 flex items-center gap-2"><Globe size={14} /> {t('languagesLabel')}</h3>
                                    <div className="space-y-1.5">
                                      {data.languages?.map((lang, i) => (
                                        <div key={i} className="flex justify-between items-center text-sm font-bold text-zinc-600 uppercase">
                                          <span>{lang ? lang.split('(')[0] : ''}</span>
                                          <span className="text-[9px] text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">{lang && lang.includes('(') ? (lang.split('(')[1] || '').replace(')', '') : 'Niveau'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </section>
                                  <section className="px-2">
                                    <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-2 flex items-center gap-2"><Plus size={14} /> {t('hobbies.label')}</h3>
                                    <div className="flex flex-wrap gap-2">
                                      {data.hobbies?.map((hobby, i) => (
                                        <span key={i} className="text-[9px] font-black uppercase text-zinc-400 border border-zinc-200 px-2 py-1 rounded">{hobby}</span>
                                      ))}
                                    </div>
                                  </section>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Mode continu graphique
                  <div>
                    <div
                      ref={cvRef}
                      className="cv-container relative"
                      style={{ background: '#fff', color: '#1a1a1a', ...marginStyles[marginSize] }}
                    >
                      {/* Executive Header Decoration */}
                      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 -translate-y-1/2 translate-x-1/2 rounded-full z-0 opacity-50 print-hidden-ats"></div>
    
                      <div className="relative z-10 flex flex-col min-h-full">
                        {/* CV Header */}
                        <header className="mb-2 flex justify-between items-start border-b-2 border-zinc-900 pb-2">
                          <div className="flex-1">
                            {((fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.')) || (fieldVisibility.firstName && data.personalInfo.firstName) || (fieldVisibility.lastName && data.personalInfo.lastName)) ? (
                              <h1 className="text-4xl font-black text-zinc-900 tracking-tighter uppercase leading-none mb-1">
                                {fieldVisibility.civility && (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.') && <>{data.personalInfo.civility} </>}
                                {fieldVisibility.firstName && data.personalInfo.firstName}
                                {(fieldVisibility.firstName && data.personalInfo.firstName) && (fieldVisibility.lastName && data.personalInfo.lastName) ? ' ' : null}
                                {fieldVisibility.lastName && data.personalInfo.lastName}
                              </h1>
                            ) : null}
                            <h2 className="text-xl font-bold text-blue-600 flex items-center gap-2">
                              {data.personalInfo.title}
                              <div className="h-0.5 bg-blue-600 flex-1 opacity-20"></div>
                            </h2>
                          </div>
    
                          {fieldVisibility.avatar && (data.personalInfo.avatar || commonInfo.compressedAvatar) && (
                            <div className="w-24 h-24 ml-8 relative">
                              <div className="absolute inset-0 bg-blue-600 rounded-3xl rotate-6 -z-10 translate-x-1 translate-y-1 scale-95 opacity-20 print:hidden"></div>
                              <div className="w-full h-full rounded-2xl overflow-hidden border-4 border-white shadow-xl print:shadow-none bg-zinc-50">
                                <img
                                  src={data.personalInfo.avatar || commonInfo.compressedAvatar}
                                  alt="Avatar"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </div>
                          )}
                        </header>
    
                        <div className="grid grid-cols-12 gap-6 flex-1">
                          {/* Main Column */}
                          <div className="col-span-8 space-y-4">
                            {/* Summary Section */}
                            <section>
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-6 h-6 bg-zinc-900 text-white rounded flex items-center justify-center"><Award size={14} /></div>
                                <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-zinc-900">{t('sections.profil')}</h3>
                              </div>
                              <div className="bg-zinc-50 p-4 rounded-2xl border-l-4 border-zinc-900 relative">
                                <div className="absolute top-2 right-4 text-4xl text-zinc-200 font-serif opacity-50">"</div>
                                <p className="text-zinc-600 leading-snug text-[13px] font-medium italic">
                                  {data.personalInfo.summary}
                                </p>
                              </div>
                            </section>
    
                            {/* Experience Section */}
                            <section>
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 bg-zinc-900 text-white rounded flex items-center justify-center"><Briefcase size={14} /></div>
                                <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-zinc-900">{t('sections.experiences')}</h3>
                              </div>
                              <div className="space-y-2">
                                {data.experience?.map((exp, i) => {
                                  const city = extractCityFromCompany(exp.company);
                                  const companyName = city ? exp.company.replace(`, ${city}`, '').trim() : exp.company;
                                  const formattedStartDate = formatDateWithFullMonth(exp.startDate);
                                  const formattedEndDate = formatDateWithFullMonth(exp.endDate);

                                  return (
                                    <div key={i} className="group cursor-default mb-2">
                                      <div className="mb-0.5">
                                        <h4 className="text-[14px] font-black text-zinc-900 group-hover:text-blue-600 transition-colors uppercase tracking-tight leading-tight">
                                          {exp.position}
                                        </h4>
                                        {(companyName || city || formattedStartDate || formattedEndDate) && (
                                          <p className="text-sm text-zinc-500 leading-tight">
                                            {companyName && <span className="text-zinc-600 font-medium">{t('experience.atCompany')} {companyName}</span>}
                                            {city && <span>{companyName ? ', ' : ''}{t('experience.inCity')} {city}</span>}
                                            {(formattedStartDate || formattedEndDate) && (
                                              <span className="text-zinc-400">
                                                {(companyName || city) ? ', ' : ''}{formattedStartDate} — {formattedEndDate}
                                              </span>
                                            )}
                                          </p>
                                        )}
                                      </div>
                                      <p className="text-zinc-600 text-[11.5px] whitespace-pre-wrap leading-tight">
                                        {exp.description}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            </section>
                          </div>
    
                          {/* Sidebar Column */}
                          <div className="col-span-4 space-y-3">
                            {/* Contact Section */}
                            <section className="bg-zinc-100 p-2.5 rounded-2xl border border-zinc-200">
                              <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-1.5 flex items-center gap-2">
                                <User size={12} /> Contact
                              </h3>
                              <div className="space-y-2">
                                {fieldVisibility.email && data.personalInfo.email && (
                                  <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><Mail size={10} /></div>
                                    <span className="text-sm font-bold text-zinc-600">{data.personalInfo.email}</span>
                                  </div>
                                )}
                                {fieldVisibility.phone && data.personalInfo.phone && (
                                  <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><Phone size={10} /></div>
                                    <span className="text-sm font-bold text-zinc-600">{data.personalInfo.phone}</span>
                                  </div>
                                )}
                                {(fieldVisibility.city || fieldVisibility.country) && getFormattedLocation() && (
                                  <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><MapPin size={10} /></div>
                                    <span className="text-sm font-bold text-zinc-600">{getFormattedLocation()}</span>
                                  </div>
                                )}
                                {/* Né à X, Nationalité (combinés) ou juste Nationalité si pas de birthPlace */}
                                {(fieldVisibility.birthPlace && data.personalInfo.birthPlace) ? (
                                  <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><MapPin size={10} /></div>
                                    <span className="text-sm font-bold text-zinc-600">{getBornPrefix()} à {data.personalInfo.birthPlace}{fieldVisibility.nationality && data.personalInfo.nationality ? `, ${data.personalInfo.nationality}` : ''}</span>
                                  </div>
                                ) : fieldVisibility.nationality && data.personalInfo.nationality ? (
                                  <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><Globe size={10} /></div>
                                    <span className="text-sm font-bold text-zinc-600">{data.personalInfo.nationality}</span>
                                  </div>
                                ) : null}
                                {/* Genre, âge, statut sur une même ligne */}
                                {(() => {
                                  const personalParts: string[] = [];
                                  if (fieldVisibility.gender && data.personalInfo.gender) personalParts.push(data.personalInfo.gender);
                                  if (fieldVisibility.birthDate && data.personalInfo.birthDate) {
                                    const age = calculateAge(data.personalInfo.birthDate);
                                    personalParts.push(age ? `${age} ans` : data.personalInfo.birthDate);
                                  }
                                  if (fieldVisibility.maritalStatus && data.personalInfo.maritalStatus) personalParts.push(data.personalInfo.maritalStatus);
                                  return personalParts.length > 0 ? (
                                    <div className="flex items-center gap-3">
                                      <div className="w-6 h-6 shrink-0 bg-white rounded flex items-center justify-center text-blue-600 shadow-sm"><User size={10} /></div>
                                      <span className="text-sm font-bold text-zinc-600">{personalParts.join(', ')}</span>
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            </section>
    
                            {/* Expertise Section */}
                            <section className="bg-zinc-900 text-white p-3.5 rounded-3xl shadow-xl print:shadow-none">
                              <h3 className="text-sm font-black uppercase tracking-[0.2em] text-blue-400 mb-1.5 flex items-center gap-2">
                                <Wrench size={12} /> Expertises
                              </h3>
                              <div className="space-y-2">
                                {data.skills?.map((skill, i) => (
                                  <div key={i} className="flex items-center gap-3">
                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    <span className="text-sm font-bold text-zinc-300 uppercase leading-none">{skill}</span>
                                  </div>
                                ))}
                              </div>
                            </section>
    
                            {/* Education Section */}
                            <section className="px-2">
                              <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-2 flex items-center gap-2">
                                <GraduationCap size={14} /> Formation
                              </h3>
                              <div className="space-y-1.5">
                                {data.education?.map((edu, i) => (
                                  <div key={i} className="relative pl-3 border-l-2 border-zinc-100">
                                    <h4 className="font-black text-zinc-900 text-sm uppercase tracking-tight leading-tight mb-0.5">{edu.degree}</h4>
                                    <div className="text-zinc-400 font-bold text-[9px] mb-0.5">{edu.school}</div>
                                    <div className="text-[9px] font-black text-blue-600 border border-blue-100 px-1 inline-block rounded">{formatEducationDate(edu)}</div>
                                  </div>
                                ))}
                              </div>
                            </section>
    
                            {/* Languages */}
                            <section className="px-2">
                              <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-2 flex items-center gap-2">
                                <Globe size={14} /> Langues
                              </h3>
                              <div className="space-y-1.5">
                                {data.languages?.map((lang, i) => (
                                  <div key={i} className="flex justify-between items-center text-sm font-bold text-zinc-600 uppercase">
                                    <span>{lang ? lang.split('(')[0] : ''}</span>
                                    <span className="text-[9px] text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">{lang && lang.includes('(') ? (lang.split('(')[1] || '').replace(')', '') : 'Niveau'}</span>
                                  </div>
                                ))}
                              </div>
                            </section>
    
                            {/* Hobbies Section */}
                            <section className="px-2">
                              <h3 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900 mb-2 flex items-center gap-2">
                                <Plus size={14} /> Passions
                              </h3>
                              <div className="flex flex-wrap gap-2">
                                {data.hobbies?.map((hobby, i) => (
                                  <span key={i} className="text-[9px] font-black uppercase text-zinc-400 border border-zinc-200 px-2 py-1 rounded">
                                    {hobby}
                                  </span>
                                ))}
                              </div>
                            </section>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// eslint-disable-next-line react-refresh/only-export-components
export { ProLayout, TypstPreview, marginStyles };
